import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { setNotifications, incrementNotificationCount } from '../redux/notificationsSlice';
import {
  Menu, Bell, LogOut, Search, Settings, Globe, User, Key, Info,
  HardDrive, Wrench, ShoppingCart, BarChart, ShieldAlert, ChevronRight, X, ChevronDown, Type, Plus, Minus,
  Cpu, Server, Database, Monitor, CheckCircle2, HelpCircle, // 🎯 أيقونات المودال الجديد
  PieChart, Construction, FileText, FileCheck, Warehouse, // 🎯 أيقونات مجموعات المنيو الجديدة
  // 🎯 أيقونات التطبيقات نفسها - نفس اللي في الـ Sidebar
  Forklift, Map as MapIcon, BookCopy, Gauge, BadgeAlert, CalendarClock, Hammer, Users, ClipboardList, Ticket,
  Touchpad, Package, Shuffle, FileBox, FileBadge, UserCheck, Cog, Kanban, HardHat, AlertTriangle,
  ShieldCheck, Tags, Book, FilePlus, Building2, MousePointer2, ListTree, SlidersHorizontal, FileUp,
  FileStack, Zap, FileCode2, Blocks
} from 'lucide-react';
import { resolveIcon } from './iconResolver';
import api from './axiosConfig';
import PersonalInformationModal  from './PersonalInformationModal';
import ChangePasswordModal from './ChangePasswordModal';
import BRAND from '../brand';

import { useFontSize } from './FontSizeContext';
import { useAuth } from './AuthContext';
import { isIdleExpired } from './sessionActivity';
import { getHelpUrl } from '../lib/helpMap';
import { useCustomAppsMenu } from './useCustomAppsMenu';

// =========================================================================
// 🎯 مكون المودال السحري لمعلومات النظام (System Info Modal)
// =========================================================================
const SystemInfoModal = ({ isOpen, onClose, t, isRTL }) => {
  if (!isOpen) return null;

  const SystemInfoCard = ({ icon, title, value, status, statusColor }) => (
    <div className="bg-surface-card p-3 rounded-xl border border-line shadow-[0_2px_10px_rgba(0,0,0,0.02)] flex items-center gap-3 relative overflow-hidden group hover:border-sky hover:shadow-md transition-all duration-300 cursor-default">
      <div className="w-10 h-10 rounded-lg bg-surface-panel flex items-center justify-center text-primary group-hover:bg-primary-soft group-hover:scale-110 transition-all duration-300">
        {icon}
      </div>
      <div className={`flex flex-col z-10 ${isRTL ? 'text-right' : 'text-left'}`}>
        <span className="text-[9px] font-black text-ink-secondary uppercase tracking-widest mb-0.5">{title}</span>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-bold text-ink font-mono">{value}</span>
          {status && (
            <span className="flex items-center gap-1 bg-surface-panel px-1.5 py-0.5 rounded-md border border-line-subtle">
              <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${statusColor}`}></span>
              <span className="text-[8px] font-black text-ink-secondary uppercase tracking-tighter">{status}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300" onClick={onClose}>
      <div 
        className="bg-surface-panel rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden relative flex flex-col outline-none transform transition-all"
        onClick={e => e.stopPropagation()} // منع الإغلاق عند الضغط داخل المودال
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header - Gradient with Tech Pattern */}
        <div className="bg-gradient-to-br from-primary-dark via-primary to-sky p-8 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 opacity-10 rotate-12 scale-150"><Cpu size={200} /></div>
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-surface-panel to-transparent opacity-20"></div>
          
          <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors bg-black/20 hover:bg-black/40 p-2 rounded-full backdrop-blur-md">
            <X size={16} />
          </button>
          
          <div className="relative z-10 flex items-center gap-4">
            <div className="w-16 h-16 bg-surface-card/10 backdrop-blur-md border border-white/20 rounded-2xl flex items-center justify-center shadow-lg">
              <img src="/assets/teknova.png" alt="Logo" className="w-10 h-auto object-contain brightness-0 invert" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-widest uppercase mb-1 drop-shadow-md">{BRAND.name}</h2>
              <p className="text-white/70 text-[10px] font-bold tracking-[0.3em] uppercase flex items-center gap-2">
                Enterprise Asset Management
                <span className="bg-white/20 border border-white/30 text-white px-2 py-0.5 rounded-full text-[8px] tracking-widest">PRO</span>
              </p>
            </div>
          </div>
        </div>

        {/* Body - Grid of System Specs */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <SystemInfoCard icon={<Monitor size={20} />} title={t('sys_info.frontend', 'Frontend Client')} value="v2.4.0-stable" />
            <SystemInfoCard icon={<Server size={20} />} title={t('sys_info.backend', 'Backend API')} value="v2.1.8-fastapi" />
            <SystemInfoCard icon={<Database size={20} />} title={t('sys_info.database', 'Database Engine')} value="PostgreSQL 15" status="Connected" statusColor="bg-status-success" />
            <SystemInfoCard icon={<Globe size={20} />} title={t('sys_info.environment', 'Environment')} value="Production Cloud" status="Live" statusColor="bg-status-success" />
          </div>

          <div className="bg-surface-card border border-line rounded-xl p-4 flex items-start gap-4 shadow-sm relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-1 h-full bg-primary"></div>
            <div className="bg-primary-soft p-2 rounded-lg shrink-0">
              <ShieldAlert className="text-primary" size={20} />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black text-primary uppercase tracking-widest">{t('sys_info.license', 'License Information')}</span>
              <p className="text-[11px] text-ink-secondary font-medium leading-relaxed">
                {t('sys_info.license_text', 'This product is licensed to your organization. Unauthorized reproduction, distribution, or reverse engineering of this software is strictly prohibited under intellectual property laws.')}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-surface-card p-4 px-6 flex justify-between items-center border-t border-line">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-status-success" />
            <span className="text-[9px] font-black text-ink-secondary uppercase tracking-widest">System Operational</span>
          </div>
          <span className="text-[9px] font-bold text-ink-secondary uppercase tracking-widest">© {new Date().getFullYear()} {BRAND.name}</span>
        </div>
      </div>
    </div>
  );
};
// =========================================================================

// 🎯 رابط سوبرست: نفس المصدر الموحود اللي Dashboard.js بيستخدمه (config.js)،
// ولو مش موجود بنرجع للاشتقاق من الدومين الحالي (app.x.com -> bi.x.com) كـ fallback بس
const getBiUrl = () => {
  if (window.RUNTIME_ENV && window.RUNTIME_ENV.SUPERSET_DOMAIN) {
    return `${window.RUNTIME_ENV.SUPERSET_DOMAIN}/`;
  }
  const parts = window.location.hostname.split('.');
  if (parts.length > 2) {
    parts[0] = 'bi';
  } else {
    parts.unshift('bi');
  }
  return `https://${parts.join('.')}/`;
};

const Header = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const { apps: customApps } = useCustomAppsMenu();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isSystemInfoOpen, setIsSystemInfoOpen] = useState(false); // 🎯 State المودال الجديد
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false); 
  const [userData, setUserData] = useState({ name: 'Guest', role: 'User', initial: 'G' });
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  
  const langMenuRef = useRef(null); 
  const menuRef = useRef(null);
  const userMenuRef = useRef(null); 

  const [isNotifMenuOpen, setIsNotifMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifMenuRef = useRef(null);

  // 🎯 زرار المساعدة (F1 + النافبار) - بيفتح صفحة المساعدة المطابقة للشاشة الحالية
  // في نفس لغة النظام، في تاب جديدة
  const openHelp = () => {
    const url = getHelpUrl(location.pathname, i18n.language);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    const handleF1 = (e) => {
      if (e.key === 'F1') {
        e.preventDefault();
        openHelp();
      }
    };
    window.addEventListener('keydown', handleF1);
    return () => window.removeEventListener('keydown', handleF1);
  }, [location.pathname, i18n.language]);

  // 1️⃣ ربط الـ WebSocket واستقبال الإشعارات لحظياً
  useEffect(() => {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      
      const { username } = JSON.parse(storedUser);
      const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      const currentHost = window.location.host;
      const wsUrl = `${wsProtocol}${currentHost}/ws/${username}`;
      const ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
          const data = JSON.parse(event.target ? event.target.data : event.data);
          setNotifications(prev => [data, ...prev]);
          setUnreadCount(prev => prev + 1);
      };

      return () => ws.close();
  }, []);

  // 2️⃣ جلب تاريخ الإشعارات
  useEffect(() => {
      const fetchNotifications = async () => {
          try {
              const res = await api.get('/notifications/unread');
              setNotifications(res.data);
              setUnreadCount(res.data.length);
          } catch (err) {
              console.error("Error fetching notifications", err);
          }
      };
      fetchNotifications();
  }, []);

  const handleNotifClick = async (notif) => {
      try {
          await api.post(`/notifications/mark-read/${notif.id}`);
          setNotifications(prev => prev.filter(n => n.id !== notif.id));
          setUnreadCount(prev => Math.max(0, prev - 1));
          setIsNotifMenuOpen(false);
          navigate(notif.link); 
      } catch (err) {
          navigate(notif.link);
      }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token && location.pathname !== '/login') {
      navigate('/login');
    } else if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUserData({
          name: parsedUser.display_name || parsedUser.username || 'Admin',
          role: parsedUser.role.name || 'Administrator',
          initial: (parsedUser.display_name || parsedUser.username || 'A').charAt(0).toUpperCase()
        });
      } catch (e) {
        console.error("Error parsing user data");
      }
    }
  }, [navigate, location.pathname]);
  
  const FontControls = () => {
    const { fontSize, changeFontSize } = useFontSize();
  
    return (
      <div className="flex items-center gap-2 bg-surface-card/10 px-3 py-1 rounded-full border border-white/20 ml-4">
        <button onClick={() => changeFontSize(-1)} className="hover:text-status-warning transition-colors">
          <Minus size={14} />
        </button>
        <div className="flex items-center gap-1 min-w-[40px] justify-center">
          <Type size={14} />
          <span className="font-black text-[10px]">{fontSize}</span>
        </div>
        <button onClick={() => changeFontSize(1)} className="hover:text-status-warning transition-colors">
          <Plus size={14} />
        </button>
      </div>
    );
  };

  const handleLogout = () => {
    // 🎯 logout() بتاع AuthContext لازم يتنادى - شاشة الدخول بقت بتحوّل أي يوزر
    // لسه متسجل في الـ Context للداشبورد، فمسح localStorage لوحده كان هيرجّعه
    logout();
    localStorage.removeItem('siteid');
    navigate('/login');
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
  
    const checkAuth = () => {
      const currentToken = localStorage.getItem('token');
      // 🎯 التجديد بقى مشروط بنشاط المستخدم - قبل كده كان بيتجدد كل 5 دقايق
      // طول ما التاب مفتوح، فالجلسة عمرها ما كانت بتنتهي
      if (!currentToken || isIdleExpired()) {
        handleForceLogout();
        return;
      }

      api.get('/auth/check-session')
        .then(res => {
          if (res.data.access_token) {
            localStorage.setItem('token', res.data.access_token);
            console.log("Session extended automatically");
          }
        })
        .catch(err => {
          if (err.response && err.response.status === 401) {
            console.warn("Session expired. Redirecting...");
            handleForceLogout();
          }
        });
    };
  
    const handleForceLogout = () => {
      // logout() بتاع AuthContext بيصفّر اليوزر كمان - من غيره الـ Context كان
      // بيفضل شايف يوزر مسجل بعد ما التوكن اتمسح
      logout();
      localStorage.removeItem('siteid');
      navigate('/login');
    };
  
    if (token) {
      checkAuth();
    } else if (location.pathname !== '/login') {
      handleForceLogout();
    }
  
    const authInterval = setInterval(() => {
      if (location.pathname !== '/login') {
        checkAuth();
      }
    }, 300000); 
  
    return () => clearInterval(authInterval);
  
  }, [navigate, location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsMenuOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) setIsUserMenuOpen(false);
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) setIsNotifMenuOpen(false);
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) setIsLangMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🎯 نضفي مصطلح البحث في المنيو كل ما القائمة تتقفل
  useEffect(() => {
    if (!isMenuOpen) setMenuSearchTerm('');
  }, [isMenuOpen]);

  const navigationModulesConfig = useMemo(() => [
  {
      group: t('menu.assets'),
      icon: <HardDrive size={16} />,
      items: [
        { icon: Forklift, label: t('menu.asset_registry'), path: "/assets", perm: ['assets', 'read'] },
        { icon: MapIcon, label: t('menu.locations'), path: "/locations", perm: ['locations', 'read'] },
        { icon: BookCopy, label: t('menu.categories'), path: "/asset-categories", perm: ['asset-categories', 'read'] },
        { icon: Gauge, label: t('menu.meters'), path: "/meters", perm: ['meters', 'read'] },
        { icon: BadgeAlert, label: t('menu.failure_codes'), path: "/failure-codes", perm: ['failure-codes', 'read'] },
        { icon: Forklift, label: t('menu.asset_features', 'Asset Infrastructure'), path: "/features", perm: ['features', 'read'] },
      ]
    },
    {
      group: t('menu.work_management'),
      icon: <Wrench size={16} />,
      items: [
        { icon: Wrench, label: t('menu.wo_tracking'), path: "/workorders", perm: ['workorders', 'read'] },
        { icon: CalendarClock, label: t('menu.labor_reporting'), path: "/smartscheduler", perm: ['workorders', 'read'] },
      ]
    },
    {
      group: t('menu.resources'),
      icon: <Construction size={16} />,
      items: [
        { icon: Hammer, label: t('menu.crafts'), path: "/crafts", perm: ['craft', 'read'] },
        { icon: Users, label: t('menu.labor'), path: "/labors", perm: ['labor', 'read'] },
        { icon: User, label: t('menu.person'), path: "/persons", perm: ['person', 'read'] },
      ]
    },
    {
      group: t('menu.service_desk'),
      icon: <FileText size={16} />,
      items: [
        { icon: ClipboardList, label: t('menu.service_requests'), path: "/sr", perm: ['sr', 'read'] },
        { icon: Ticket, label: t('menu.incidents'), path: "/incidents", perm: ['incidents', 'read'] },
      ]
    },
    {
      group: t('menu.inventory'),
      icon: <Warehouse size={16} />,
      items: [
        { icon: Touchpad, label: t('menu.item_master'), path: "/items", perm: ['items', 'read'] },
        { icon: Hammer, label: t('menu.tools'), path: "/tools", perm: ['tools', 'read'] },
        { icon: Package, label: t('menu.balances'), path: "/inventory", perm: ['inventory', 'read'] },
        { icon: Shuffle, label: t('menu.transfers'), path: "/inventoryusage", perm: ['inventoryusage', 'read'] },
        { icon: FileText, label: t('apps.MR'), path: "/mr", perm: ['mr', 'read'] },
        { icon: Warehouse, label: t('menu.storerooms'), path: "/storerooms", perm: ['storerooms', 'read'] },
      ]
    },
    {
      group: t('menu.procurement'),
      icon: <ShoppingCart size={16} />,
      items: [
        { icon: FileBox, label: t('menu.po'), path: "/po", perm: ['po', 'read'] },
        { icon: FileBadge, label: t('menu.pr'), path: "/pr", perm: ['pr', 'read'] },
        { icon: FileBadge, label: t('menu.rfq'), path: "/rfq", perm: ['rfq', 'read'] },
        { icon: Package, label: t('menu.receiving'), path: "/receiving", perm: ['receiving', 'read'] },
        { icon: FileText, label: t('menu.invoices'), path: "/invoices", perm: ['invoices', 'read'] },
        { icon: UserCheck, label: t('menu.vendors'), path: "/vendors", perm: ['vendors', 'read'] },
      ]
    },
    {
      group: t('menu.contracts'),
      icon: <FileCheck size={16} />,
      items: [
        { icon: FileCheck, label: t('menu.contracts_master'), path: "/contracts", perm: ['contracts', 'read'] },
        { icon: FileCheck, label: t('menu.sla'), path: "/sla", perm: ['sla', 'read'] },
      ]
    },
    {
      group: t('menu.planning'),
      icon: <BarChart size={16} />,
      items: [
        { icon: Cog, label: t('menu.pm_schedules'), path: "/pm", perm: ['pm', 'read'] },
        { icon: Kanban, label: t('menu.job_plans'), path: "/jp", perm: ['jp', 'read'] },
        { icon: HardHat, label: t('menu.safety_plans'), path: "/safety", perm: ['safety', 'read'] },
        { icon: AlertTriangle, label: t('menu.hazards'), path: "/hazards", perm: ['hazards', 'read'] },
        { icon: ShieldCheck, label: t('menu.precautions'), path: "/precautions", perm: ['precautions', 'read'] },
        { icon: Tags, label: t('menu.tagouts'), path: "/tagouts", perm: ['tagouts', 'read'] },
      ]
    },
    {
      group: t('menu.iot'),
      icon: <Cpu size={16} />,
      items: [
        { icon: Cpu, label: t('menu.iot_monitoring'), path: "/iot", perm: ['iot', 'read'] },
      ]
    },
    {
      group: t('menu.security'),
      icon: <ShieldAlert size={16} />,
      items: [
        { icon: User, label: t('menu.user_list'), path: "/users", perm: ['users', 'read'] },
        { icon: Users, label: t('menu.groups'), path: "/roles", perm: ['roles', 'read'] },
      ]
    },
    {
      group: t('menu.analytics'),
      icon: <PieChart size={16} />,
      items: [
        { icon: PieChart, label: t('menu.dashboards'), path: 'external', url: getBiUrl(), perm: ['kpi', 'read'] },
        { icon: Book, label: t('menu.report_designer'), path: "/reportlist", perm: ['reportlist', 'read'] },
        { icon: FilePlus, label: t('menu.report_wizard'), path: "/reportwizard", perm: ['reportwizard', 'read'] },
      ]
    },
    {
      group: t('menu.settings'),
      icon: <Settings size={16} />,
      items: [
        { icon: Building2, label: t('menu.organization'), path: "/organizations", perm: ['organizations', 'read'] },
        { icon: MousePointer2, label: t('menu.domains'), path: "/domains", perm: ['domains', 'read'] },
        { icon: ListTree, label: t('menu.classifications'), path: "/classifications", perm: ['classifications', 'read'] },
        { icon: SlidersHorizontal, label: t('menu.field_config'), path: "/admin/field-config", perm: ['field-config', 'read'] },
        { icon: FileUp, label: t('menu.importer'), path: "/uniimporter", perm: ['uniimporter', 'read'] },
        { icon: FileStack, label: t('menu.cobie'), path: "/cobiemanager", perm: ['cobiemanager', 'read'] },
        { icon: Zap, label: t('menu.workflow'), path: "/workflow", perm: ['workflow', 'read'] },
        { icon: FileCode2, label: t('menu.scripts'), path: "/admin/workflow/studio", perm: ['scripts', 'read'] },
        { icon: Blocks, label: t('menu.custom_app_builder', 'Custom App Builder'), path: "/admin/custom-apps", perm: ['custom_apps', 'write'] },
        { icon: Database, label: "Backup Manager", path: "/admin/backups", perm: ['admin', 'read'] },
        // 🎯 إعدادات النظام (__GLOBAL__) بصلاحيتها المستقلة - زي الـ Sidebar
        { icon: Cog, label: t('menu.system_settings', 'System Settings'), path: "/admin/system-settings", perm: ['system-settings', 'update'] },
      ]
    },
    // 🎯 مجموعة التطبيقات المخصصة - بتتقرأ ديناميكيًا من /custom-apps/menu،
    // مش مكتوبة يدوي زي باقي المجموعات فوق
    ...(customApps.length > 0 ? [{
      group: t('menu.custom_apps', 'Custom Applications'),
      icon: <Cpu size={16} />,
      items: customApps.map(app => ({
        icon: resolveIcon(app.icon_name),
        label: app.translations?.[i18n.language] || t(`custom.app.${app.slug}`, app.label),
        path: `/custom/${app.slug}`,
        perm: [app.module, 'read'],
      })),
    }] : [])
  ], [t, customApps]);

  const filteredNavigation = useMemo(() => {
    const term = menuSearchTerm.trim().toLowerCase();
    return navigationModulesConfig.map(module => {
      const permittedItems = module.items.filter(item => hasPermission(item.perm[0], item.perm[1]));
      if (!term) {
        return permittedItems.length > 0 ? { ...module, items: permittedItems } : null;
      }
      // لو اسم المجموعة نفسه بيطابق البحث، اعرض كل عناصرها المسموح بيها
      if (module.group.toLowerCase().includes(term)) {
        return permittedItems.length > 0 ? { ...module, items: permittedItems } : null;
      }
      const matchedItems = permittedItems.filter(item => item.label.toLowerCase().includes(term));
      return matchedItems.length > 0 ? { ...module, items: matchedItems } : null;
    }).filter(module => module !== null);
  }, [navigationModulesConfig, hasPermission, menuSearchTerm]);

  return (
    <header className="h-10 bg-surface-panel border-b border-line flex items-center justify-between px-4 shadow-sm z-[2000] relative select-none">
      
      {/* 🎯 استدعاء مودال النظام */}
      <SystemInfoModal 
        isOpen={isSystemInfoOpen} 
        onClose={() => setIsSystemInfoOpen(false)} 
        t={t} 
        isRTL={i18n.language === 'ar' || i18n.language === 'ur'}
      />

      {isPersonalModalOpen && (
        <PersonalInformationModal 
          isOpen={isPersonalModalOpen}
          onClose={() => setIsPersonalModalOpen(false)} 
        />
      )}
      {isChangePasswordOpen && (
        <ChangePasswordModal
          isOpen={isChangePasswordOpen}
          onClose={() => setIsChangePasswordOpen(false)}
        />
      )}

      {/* القسم الأيسر: أيقونة المنيو وشعار MAXEAM */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? t('common.close_menu', 'Close menu') : t('common.open_menu', 'Open menu')}
          aria-expanded={isMenuOpen}
          className={`p-1.5 rounded transition-all flex items-center gap-2 group ${isMenuOpen ? 'bg-primary text-white shadow-inner' : 'text-primary hover:bg-primary-soft'}`}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="h-6 w-[1px] bg-line mx-2"></div>
        
        <div 
          className="flex items-center cursor-pointer group" 
          onClick={() => { navigate('/'); setIsMenuOpen(false); }}
        >
          <span><img src="/assets/teknova.png" alt={`${BRAND.name} Logo`} className="h-5 w-auto object-contain" /></span>
          <span style={{ marginInlineStart: '0.5rem' }} className="text-[9px] text-primary font-bold uppercase tracking-[0.2em] hidden md:block opacity-70 group-hover:opacity-100 transition-opacity">{t('common.asset_management', 'Asset Management')}</span>
        </div>
      </div>

      {/* Mega Menu */}
      {isMenuOpen && (
        <div 
          ref={menuRef}
          className={`absolute top-10 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'right-0' : 'left-0'} w-full md:w-[850px] bg-surface-card shadow-[0_20px_50px_rgba(0,0,0,0.2)] border-x border-b border-line animate-in slide-in-from-top duration-200 z-[9999] rounded-b-md overflow-hidden text-start`}
        >
          {/* Header المنيو مع البحث */}
          <div className="grid grid-cols-1 md:grid-cols-3 bg-surface-panel border-b border-line-subtle">
             <div className="p-3 flex items-center gap-3 border-e border-line">
                <div className="w-8 h-8 bg-primary-soft rounded-full flex items-center justify-center text-primary"><Settings size={18}/></div>
                <div className="flex flex-col">
                   <span className="text-[11px] font-black text-primary uppercase">Start Center</span>
                   <span className="text-[9px] text-ink-secondary">Manage your workspace</span>
                </div>
             </div>
             <div className="col-span-2 p-2 px-4 flex items-center">
                <div className="relative w-full">
                   <Search size={14} className="absolute inset-y-0 start-3 my-auto text-ink-secondary" />
                   <input
                      type="text"
                      autoFocus
                      value={menuSearchTerm}
                      onChange={(e) => setMenuSearchTerm(e.target.value)}
                      placeholder={t('search.placeholder')}
                      className="w-full bg-surface-card border border-line rounded-full py-2 ps-10 pe-4 text-[11px] focus:border-primary outline-none transition-all shadow-sm"
                   />
                   {menuSearchTerm && (
                     <button
                       type="button"
                       onClick={() => setMenuSearchTerm('')}
                       className="absolute inset-y-0 end-3 my-auto text-ink-secondary hover:text-primary transition-colors"
                       aria-label={t('common.clear', 'Clear')}
                     >
                       <X size={12} />
                     </button>
                   )}
                </div>
             </div>
          </div>

          {/* محتوى الموديولات - 🎯 أعمدة (Masonry) بدل Grid: كل صف في الـ Grid كان
              بياخد طول أطول مجموعة فيه، فالمجموعات القصيرة (IOT/Security/Contracts)
              بتسيب فراغ كبير تحتها. الأعمدة بترص المجموعات تحت بعض وتوازن الأطوال،
              فالقصيرة بتملا الفراغات ومحتاجين اسكرول أقل */}
          <div className="p-2 max-h-[70vh] overflow-y-auto">
            {/* الأعمدة جوه div من غير ارتفاع محدد - وإلا المتصفح بيرص الأعمدة بالعرض بدل ما يوازنها */}
            <div className="columns-1 md:columns-3 lg:columns-4 gap-0">
            {menuSearchTerm && filteredNavigation.length === 0 && (
              <div className="[column-span:all] flex flex-col items-center justify-center gap-2 py-10 text-ink-secondary">
                <Search size={20} className="opacity-40" />
                <span className="text-[11px] font-bold">{t('search.no_results', 'No results found')}</span>
              </div>
            )}
            {filteredNavigation.map((module, idx) => (
              <div key={idx} className="break-inside-avoid p-3 hover:bg-surface-panel/80 transition-all rounded-sm">
                <div className="flex items-center gap-2 text-primary font-black uppercase text-[10px] mb-3 border-b-2 border-primary/10 pb-1.5">
                  {module.icon}
                  {module.group}
                </div>
                <div className="flex flex-col gap-0.5">
                  {module.items.map((item, i) => (
                    <div
                      key={i}
                      onClick={() => {
                        if (item.path === 'external') {
                          window.open(item.url, '_blank', 'noopener,noreferrer');
                        } else {
                          navigate(item.path);
                        }
                        setIsMenuOpen(false);
                      }}
                      className="flex items-center justify-between group cursor-pointer p-1.5 hover:bg-primary hover:text-white rounded-sm transition-all text-[11px] text-ink-secondary font-bold"
                    >
                      {/* 🎯 نفس أيقونة التطبيق في الـ Sidebar */}
                      <span className="flex items-center gap-2 tracking-tight min-w-0">
                        {item.icon && <item.icon size={13} className="shrink-0 text-primary/70 group-hover:text-white transition-colors" />}
                        <span className="truncate">{item.label}</span>
                      </span>
                      <ChevronRight size={10} className={`opacity-0 group-hover:opacity-100 transition-all transform ${i18n.language === 'ar' ? 'rotate-180 -translate-x-1' : 'translate-x-1'}`} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            </div>
          </div>
          <div className="bg-primary-dark p-2 px-6 flex justify-between items-center text-[9px] text-white/70 font-bold uppercase tracking-widest">
            <span>{t('brand.version')}</span>
          </div>
        </div>
      )}

      {/* القسم الأيمن */}
      <div className="flex items-center gap-3 h-full">
        <FontControls />
        <div className="flex items-center gap-4 text-primary border-l border-line pl-4 h-full">

          {/* --- Language Selector Dropdown --- */}
          <div className="relative flex items-center h-full" ref={langMenuRef}>
            <button 
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded transition-all ${isLangMenuOpen ? 'bg-primary-soft' : 'hover:bg-primary-soft'}`}
            >
              <Globe size={16} className="text-primary" />
              <span className="text-[10px] font-black uppercase text-primary">{i18n.language}</span>
              <ChevronDown size={10} className={`text-ink-secondary transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangMenuOpen && (
              <div className="absolute top-10 right-0 w-32 bg-surface-card shadow-xl border border-line rounded-md overflow-hidden animate-in fade-in slide-in-from-top-2 z-[5000]">
                <div className="flex flex-col py-1">
                  {[
                      { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
                      { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
                      { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
                      { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' }, 
                      { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' }, 
                      { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },  
                      { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },   
                      { code: 'ja', name: '日本語', flag: '🇯🇵', dir: 'ltr' },   
                      { code: 'ko', name: '한국어', flag: '🇰🇷', dir: 'ltr' },   
                      { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' }, 
                      { code: 'ur', name: 'اُردُو', flag: '🇵🇰', dir: 'rtl' }   
                  ].map((lang) => (
                    <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          document.documentElement.dir = lang.dir;
                          document.body.dir = lang.dir;
                          localStorage.setItem('i18nextLng', lang.code);
                          localStorage.setItem('layoutDir', lang.dir);
                          setIsLangMenuOpen(false);
                          
                          if (lang.dir === 'rtl') {
                            document.body.style.fontFamily = "'Cairo', sans-serif";
                          } else {
                            document.body.style.fontFamily = "'Inter', sans-serif";
                          }
                        }}
                        className={`flex items-center gap-3 px-4 py-2 text-[11px] font-bold transition-all hover:bg-primary hover:text-white
                          ${i18n.language === lang.code ? 'bg-primary-soft text-primary' : 'text-ink-secondary'}`}
                      >
                      <span className="text-sm">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* 🔔 قسم الجرس (Notifications) */}
          <div className="relative flex items-center h-full" ref={notifMenuRef}>
            <button
              onClick={() => setIsNotifMenuOpen(!isNotifMenuOpen)}
              aria-label={t('notifications.title')}
              className="relative p-1 text-primary hover:bg-primary-soft rounded-full transition-colors"
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-status-danger px-1 text-[9px] font-bold text-white border-2 border-surface-panel">
                  {unreadCount}
                </span>
              )}
            </button>

              {isNotifMenuOpen && (
                <div className={`absolute top-10 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'left-0' : 'right-0'} w-80 bg-surface-card shadow-2xl border border-line rounded-md overflow-hidden animate-in fade-in slide-in-from-top-2 z-[5000]`}>
                  <div className="p-3 border-b bg-surface-panel flex justify-between items-center">
                    <span className="text-[10px] font-black text-primary uppercase tracking-widest">{t('notifications.title')}</span>
                    {unreadCount > 0 && (
                      <span className="text-[9px] bg-primary-soft text-primary px-2 py-0.5 rounded-full font-bold">
                        {unreadCount} {t('notifications.new_alerts')}
                      </span>
                    )}
                  </div>

                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center flex flex-col items-center gap-2 opacity-40">
                        <Bell size={24} className="text-ink-secondary" />
                        <span className="text-[10px] font-bold text-ink-secondary uppercase">{t('notifications.empty')}</span>
                      </div>
                    ) : (
                      notifications.map((notif, idx) => (
                        <div 
                          key={idx}
                          onClick={() => handleNotifClick(notif)}
                          className="p-3 border-b border-line-subtle hover:bg-primary-soft cursor-pointer transition-all group relative"
                        >
                          <div className={`flex flex-col gap-1 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
                            <div className="flex justify-between items-start">
                              <span className="text-[11px] font-black text-ink group-hover:text-primary">{notif.title}</span>
                              <span className="text-[8px] text-ink-secondary font-bold uppercase">{notif.app_name}</span>
                            </div>
                            <p className="text-[10px] text-ink-secondary leading-tight font-medium">{notif.message}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[9px] font-black text-primary uppercase tracking-tighter">Ref: {notif.ref_id}</span>
                              <ChevronRight size={10} className={`text-primary opacity-0 group-hover:opacity-100 transition-all transform ${i18n.language === 'ar' || i18n.language === 'ur' ? 'rotate-180 -translate-x-1' : 'translate-x-1'}`} />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  <div className="p-2 border-t bg-surface-panel text-center">
                    <button className="text-[9px] font-black text-primary uppercase hover:underline">{t('notifications.view_all')}</button>
                  </div>
                </div>
              )}
            </div>

          {/* 👤 قسم ملف المستخدم (User Profile) */}
          <div className="relative h-full flex items-center" ref={userMenuRef}>
            <div 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`flex items-center gap-2 cursor-pointer border-line hover:opacity-80 transition-opacity h-full ${i18n.language === 'ar' || i18n.language === 'ur' ? 'border-l pl-4' : 'border-r pr-4'}`}
            >
              <div className="w-6 h-6 bg-primary-dark rounded-sm flex items-center justify-center text-white text-[10px] font-black shadow-md uppercase">
                {userData.initial}
              </div>
              <div className={`flex flex-col leading-none hidden sm:flex ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
                <span className="text-[10px] font-black text-primary uppercase">{userData.name}</span>
                <span className="text-[8px] text-ink-secondary font-bold uppercase">{userData.role}</span>
              </div>
              <ChevronDown size={12} className={`text-ink-secondary transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
            </div>

            {/* القائمة المنسدلة لليوزر */}
            {isUserMenuOpen && (
              <div className={`absolute top-10 ${i18n.language === 'ar' || i18n.language === 'ur' ? 'left-0' : 'right-0'} w-48 bg-surface-card shadow-xl border border-line rounded-b-md overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150 z-[3000]`}>
                <div className={`p-2 border-b bg-surface-panel ${i18n.language === 'ar' || i18n.language === 'ur' ? 'text-right' : 'text-left'}`}>
                  <span className="text-[9px] font-black text-ink-secondary uppercase tracking-widest px-2">{t('user_settings.title')}</span>
                </div>
                
                <div className="flex flex-col">
                  <button 
                    onClick={() => { setIsPersonalModalOpen(true); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-ink-secondary hover:bg-primary hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <Info size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.default_info')}</span>
                  </button>
                  {/*}
                  <button 
                    onClick={() => { navigate('/profile/personal'); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-ink-secondary hover:bg-primary hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <User size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.personal_info')}</span>
                  </button>
                  */}
                  <button 
                    onClick={() => { setIsChangePasswordOpen(true); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-ink-secondary hover:bg-primary hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <Key size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.password_info')}</span>
                  </button>

                  <div className="h-[1px] bg-surface-panel my-1"></div>

                  {/* 🎯 زرار عن النظام (System Info) */}
                  <button 
                    onClick={() => { setIsSystemInfoOpen(true); setIsUserMenuOpen(false); }}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-ink-secondary hover:bg-primary hover:text-white transition-all group ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <Cpu size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.system_info', 'عن النظام')}</span>
                  </button>

                  <div className="h-[1px] bg-surface-panel my-1"></div>

                  <button 
                    onClick={handleLogout}
                    className={`flex items-center gap-3 p-2.5 px-4 text-[11px] font-bold text-status-danger hover:bg-status-danger-bg transition-all ${i18n.language === 'ar' || i18n.language === 'ur' ? 'flex-row-reverse text-right' : 'text-left'}`}
                  >
                    <LogOut size={14} className="shrink-0" />
                    <span className="flex-1">{t('user_settings.sign_out')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* --- زرار المساعدة (Help) --- */}
          <button
            onClick={openHelp}
            className="flex items-center gap-1.5 px-2 py-1 rounded transition-all hover:bg-primary-soft"
            title={`${t('header.help', 'Help')} (F1)`}
          >
            <HelpCircle size={16} className="text-primary" />
          </button>

        </div>
      </div>
    </header>
  );
};

export default Header;